/** Tipos da API de disparos — espelho de supabase/functions/_shared/disparos-types.ts */

export type DisparoMode = "simple" | "advanced";
export type DisparoStatus =
  | "pending"
  | "active"
  | "paused"
  | "suspended"
  | "completed"
  | "failed"
  | "deleted";

export type SenderMessageType =
  | "text"
  | "image"
  | "video"
  | "videoplay"
  | "document"
  | "audio"
  | "myaudio"
  | "ptt"
  | "ptv"
  | "sticker"
  | "list"
  | "button"
  | "poll"
  | "carousel";

export interface DisparoRecord {
  id: string;
  workspace_id: string;
  integration_id: string;
  created_by: string | null;
  name: string | null;
  mode: DisparoMode;
  status: DisparoStatus;
  folder_id: string | null;
  folder_name: string | null;
  message_type: string | null;
  recipient_count: number;
  lead_ids: string[] | null;
  phones: string[];
  config: Record<string, unknown>;
  provider_response: Record<string, unknown> | null;
  scheduled_for: string | null;
  created_at: string;
  updated_at: string;
}

export interface DisparoRecipient {
  phone: string;
  lead_id?: string;
}

export interface DisparosCreateSimpleRequest {
  workspace_id: string;
  integration_id: string;
  mode: "simple";
  name?: string;
  phones: string[];
  lead_ids?: string[];
  /** Nomes dos leads, na mesma ordem de phones/lead_ids (snapshot em config.recipients). */
  names?: string[];
  type: SenderMessageType;
  text?: string;
  file?: string;
  docName?: string;
  choices?: string[];
  footerText?: string;
  listButton?: string;
  selectableCount?: number;
  imageButton?: string;
  carousel?: unknown;
  delayMin?: number;
  delayMax?: number;
  scheduled_for?: string;
  folder?: string;
  info?: string;
}

export interface DisparosCreateAdvancedRequest {
  workspace_id: string;
  integration_id: string;
  mode: "advanced";
  name?: string;
  messages: Array<{
    phone: string;
    lead_id?: string;
    name?: string;
    type: SenderMessageType;
    text?: string;
    file?: string;
    docName?: string;
    choices?: string[];
    footerText?: string;
    listButton?: string;
    selectableCount?: number;
    imageButton?: string;
    carousel?: unknown;
  }>;
  delayMin?: number;
  delayMax?: number;
  scheduled_for?: string;
  info?: string;
}

export type DisparosCreateRequest = DisparosCreateSimpleRequest | DisparosCreateAdvancedRequest;

export interface DisparosCreateResponse {
  ok: boolean;
  disparo_id?: string;
  folder_id?: string;
  recipient_count: number;
  provider?: unknown;
  error?: string;
}

export interface DisparosControlRequest {
  workspace_id: string;
  integration_id: string;
  folder_id: string;
  action: "stop" | "continue" | "delete";
  disparo_id?: string;
}

export interface DisparosControlResponse {
  ok: boolean;
  action: string;
  folder_id: string;
  provider: unknown;
  disparo_id?: string;
  error?: string;
}

export interface DisparosListFoldersRequest {
  workspace_id: string;
  integration_id: string;
  status?: "Active" | "Archived";
}

export interface DisparosListMessagesRequest {
  workspace_id: string;
  integration_id: string;
  folder_id: string;
  messageStatus?: string;
  limit?: number;
  offset?: number;
}

export type DisparosListRequest =
  | ({ action: "listfolders" } & DisparosListFoldersRequest)
  | ({ action: "listmessages" } & DisparosListMessagesRequest);

export interface DisparosListResponse {
  ok: boolean;
  action: "listfolders" | "listmessages";
  data: unknown;
  error?: string;
}

export interface DisparosCleanupClearDoneRequest {
  workspace_id: string;
  integration_id: string;
  action: "cleardone";
  hours?: number;
}

export interface DisparosCleanupClearAllRequest {
  workspace_id: string;
  integration_id: string;
  action: "clearall";
}

export type DisparosCleanupRequest =
  | DisparosCleanupClearDoneRequest
  | DisparosCleanupClearAllRequest;

export interface DisparosCleanupResponse {
  ok: boolean;
  action: "cleardone" | "clearall";
  provider: unknown;
  error?: string;
}

// ─── Tipos de UI (frontend) ──────────────────────────────────────────────────

export type DisparoMessageType =
  | "text"
  | "image"
  | "video"
  | "audio"
  | "document"
  | "contact"
  | "location"
  | "list"
  | "button"
  | "poll"
  | "carousel";

export type DisparoCampaignStatus =
  | "draft"
  | "scheduled"
  | "running"
  | "paused"
  | "suspended"
  | "completed"
  | "failed"
  | "cancelled";

export type DisparoMessageStatus =
  | "pending"
  | "queued"
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "skipped";

export interface DisparoLead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  stage: string | null;
  funnel_id: string | null;
  /** Nome legível do funil (preenchido na UI ao listar). */
  funnel_name?: string | null;
  /** Nome legível da coluna/estágio (preenchido na UI ao listar). */
  stage_name?: string | null;
  /** Ordem da coluna no funil — usado para agrupar seções. */
  stage_order_index?: number | null;
}

export interface DisparoLeadFilters {
  funnelId: string | null;
  stageSlug: string | null;
  funnelName?: string | null;
  stageName?: string | null;
  tagIds: string[];
  search: string;
}

export interface DisparoMessagePayload {
  type: DisparoMessageType;
  text?: string;
  file?: string;
  docName?: string;
  caption?: string;
  extra?: Record<string, unknown>;
}

export interface DisparoSimpleRecipient {
  lead_id: string;
  phone: string;
  name?: string;
}

export interface DisparoAdvancedRecipient extends DisparoSimpleRecipient {
  message: DisparoMessagePayload;
}

export interface CreateSimpleDisparoPayload {
  workspace_id: string;
  integration_id: string;
  campaign_name: string;
  message: DisparoMessagePayload;
  recipients: DisparoSimpleRecipient[];
  delay_min_seconds?: number;
  delay_max_seconds?: number;
  scheduled_at?: string | null;
  /** Período diário de envio; divide a campanha em lotes por dia. */
  send_window?: import("./disparos-window").DisparoSendWindow | null;
}

export interface CreateAdvancedDisparoPayload {
  workspace_id: string;
  integration_id: string;
  campaign_name: string;
  recipients: DisparoAdvancedRecipient[];
  delay_min_seconds?: number;
  delay_max_seconds?: number;
  scheduled_at?: string | null;
  send_window?: import("./disparos-window").DisparoSendWindow | null;
}

export interface DisparoCampaign {
  id: string;
  name: string;
  status: DisparoCampaignStatus;
  integration_id: string;
  total: number;
  sent: number;
  failed: number;
  pending: number;
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
  /** Motivo da suspensão automática (conexão caiu), quando houver. */
  suspended_reason?: string | null;
}

export interface RescheduleDisparoPayload {
  integration_id: string;
  scheduled_at: string;
}

export interface DisparoCampaignMessage {
  id: string;
  campaign_id: string;
  lead_id: string | null;
  phone: string;
  lead_name: string | null;
  status: DisparoMessageStatus;
  error: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface DisparoMessageFilters {
  status?: DisparoMessageStatus | "all";
  search?: string;
  limit?: number;
  offset?: number;
}

export interface DisparoCleanupOptions {
  delete_sent?: boolean;
  delete_failed?: boolean;
  delete_pending?: boolean;
  older_than_days?: number;
}

export const DISPARO_MESSAGE_TYPE_LABELS: Record<DisparoMessageType, string> = {
  text: "Texto",
  image: "Imagem",
  video: "Vídeo",
  audio: "Áudio",
  document: "Documento",
  contact: "Contato",
  location: "Localização",
  list: "Lista",
  button: "Botões",
  poll: "Enquete",
  carousel: "Carrossel",
};

export const DISPARO_STATUS_LABELS: Record<DisparoCampaignStatus, string> = {
  draft: "Rascunho",
  scheduled: "Agendada",
  running: "Em andamento",
  paused: "Pausada",
  suspended: "Suspensa",
  completed: "Concluída",
  failed: "Falhou",
  cancelled: "Cancelada",
};

export const DISPARO_MESSAGE_STATUS_LABELS: Record<DisparoMessageStatus, string> = {
  pending: "Pendente",
  queued: "Na fila",
  sent: "Enviada",
  delivered: "Entregue",
  read: "Lida",
  failed: "Falhou",
  skipped: "Ignorada",
};
